import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import {
  Auction,
  ClinicalConsultation,
  DocsFormName,
  Patient,
} from "core/types";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RecareDocsModal } from "./Modal";

type RecareDocsContextType = {
  formModal: DocsFormName | null;
  setFormModal: (value: DocsFormName | null) => void;
};

type RecareDocsProviderProps = {
  children: ReactNode;
  auction?: Auction;
  consultation?: ClinicalConsultation;
  patient: Patient;
};

const RecareDocsContext = createContext<RecareDocsContextType | undefined>(
  undefined,
);

export const RecareDocsProvider = ({
  auction,
  children,
  consultation,
  patient,
}: RecareDocsProviderProps) => {
  const [formModal, setFormModal] = useState<DocsFormName | null>(null);
  const { isDischargeLetterActive, isDocsAndExtractActive, isRedesignActive } =
    useIsDocsAndExtractActive();
  const { extractOcrStatus } = useExtractDocs();

  useEffect(() => {
    if (isDocsAndExtractActive && formModal !== null) {
      extractOcrStatus.refetch();
    }
  }, [formModal, isDocsAndExtractActive, extractOcrStatus.refetch]);

  const context = useMemo(
    () => ({
      formModal,
      setFormModal,
    }),
    [formModal],
  );

  return (
    <RecareDocsContext.Provider value={context}>
      {children}
      {(isRedesignActive || isDischargeLetterActive) && (
        <RecareDocsModal
          auction={auction}
          consultation={consultation}
          patient={patient}
        />
      )}
    </RecareDocsContext.Provider>
  );
};

export const useRecareDocsContext = (): RecareDocsContextType => {
  const { isDischargeLetterActive, isRedesignActive } =
    useIsDocsAndExtractActive();
  const context = useContext(RecareDocsContext);

  if (context === undefined) {
    if (isRedesignActive || isDischargeLetterActive) {
      throw new Error(
        "useRecareDocsContext must be used within a RecareDocsProvider",
      );
    }
    return {
      formModal: null,
      setFormModal: () => {},
    };
  }
  return context;
};
